import { useState } from 'react';
import {
  validateSignup,
  suggestSlug,
  siteUrlFor,
  type SignupForm,
  type Country,
} from '../lib/signup';
import { registerRestaurant } from '../lib/signupService';
import PasswordInput from './PasswordInput';

/**
 * Page d'inscription libre-service (Brique 3, phase A).
 * Le restaurateur remplit le formulaire → compte + resto + menu exemple créés,
 * site « live » sur le socle multi-resto. Logique dans signup.ts / signupService.ts (testées).
 *
 * Phase de lancement : accès SUR INVITATION. Mettre INVITATION_CODES à null
 * pour ouvrir l'inscription à tous (aucun autre changement nécessaire).
 */
const INVITATION_CODES: string[] | null = ['LILCO2026'];

export default function Signup() {
  const [form, setForm] = useState<SignupForm>({
    restaurantName: '',
    email: '',
    password: '',
    country: 'fr',
    slug: '',
    invitationCode: '',
  });
  const [slugEdited, setSlugEdited] = useState(false);
  const [errors, setErrors] = useState<ReturnType<typeof validateSignup>['errors']>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ siteUrl: string; sitePath?: string } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const set = (patch: Partial<SignupForm>) => setForm((f) => ({ ...f, ...patch }));

  // Le slug se déduit du nom tant que l'utilisateur ne l'a pas modifié à la main.
  const onName = (restaurantName: string) =>
    set(slugEdited ? { restaurantName } : { restaurantName, slug: suggestSlug(restaurantName) });

  const submit = async () => {
    setServerError(null);
    const v = validateSignup(form, INVITATION_CODES);
    setErrors(v.errors);
    if (!v.ok) return;

    setBusy(true);
    try {
      const res = await registerRestaurant(form);
      if (res.ok && res.siteUrl) setDone({ siteUrl: res.siteUrl, sitePath: res.sitePath });
      else setServerError(res.error ?? 'Une erreur est survenue.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="max-w-md w-full text-center py-10">
        <div className="text-5xl mb-3">🎉</div>
        <h2 className="text-xl font-bold text-brand-primary mb-2">Votre restaurant est en ligne !</h2>
        {done.sitePath && (
          <p className="opacity-80 mb-4">
            Votre site est accessible tout de suite ici :<br />
            <a
              href={done.sitePath}
              className="font-semibold text-brand-primary underline break-all"
            >
              {window.location.origin}
              {done.sitePath}
            </a>
          </p>
        )}
        <p className="text-xs opacity-60 mb-4">
          Adresse personnalisée à venir : <span className="font-medium">{done.siteUrl}</span>{' '}
          (une fois le domaine connecté).
        </p>
        <p className="text-sm opacity-70">
          Connectez-vous à votre espace pour personnaliser votre menu, vos photos et vos options.
        </p>
      </section>
    );
  }

  const field = 'w-full border border-slate-200 rounded-lg p-2.5 mt-1';
  const errText = (k: keyof SignupForm) =>
    errors[k] ? <p className="text-xs text-red-600 mt-1">{errors[k]}</p> : null;

  return (
    <section className="max-w-md w-full flex flex-col gap-3">
      <h2 className="text-xl font-bold text-brand-primary">Créer mon restaurant</h2>
      <p className="text-sm opacity-70 -mt-1">
        Quelques infos et votre site Click &amp; Collect est prêt en quelques secondes.
      </p>

      <label className="text-sm font-medium">
        Nom du restaurant
        <input className={field} value={form.restaurantName} onChange={(e) => onName(e.target.value)}
          placeholder="Ex. Burger House" />
        {errText('restaurantName')}
      </label>

      <label className="text-sm font-medium">
        Pays
        <select className={field} value={form.country}
          onChange={(e) => set({ country: e.target.value as Country })}>
          <option value="fr">France (€)</option>
          <option value="be">Belgique (€)</option>
          <option value="ch">Suisse (CHF)</option>
        </select>
      </label>

      <label className="text-sm font-medium">
        Adresse de votre site
        <input className={field} value={form.slug}
          onChange={(e) => { setSlugEdited(true); set({ slug: e.target.value.toLowerCase() }); }}
          placeholder="mon-resto" />
        {form.slug && !errors.slug && (
          <p className="text-xs opacity-60 mt-1">→ {siteUrlFor(form.slug, form.country)}</p>
        )}
        {errText('slug')}
      </label>

      <label className="text-sm font-medium">
        E-mail
        <input className={field} type="email" value={form.email}
          onChange={(e) => set({ email: e.target.value })} placeholder="vous@restaurant.fr" />
        {errText('email')}
      </label>

      <label className="text-sm font-medium">
        Mot de passe
        <PasswordInput value={form.password}
          onChange={(e) => set({ password: e.target.value })} autoComplete="new-password" placeholder="8 caractères minimum" />
        {errText('password')}
      </label>

      {INVITATION_CODES !== null && (
        <label className="text-sm font-medium">
          Code d'invitation
          <input className={field} value={form.invitationCode}
            onChange={(e) => set({ invitationCode: e.target.value })} placeholder="Reçu par Lil & Co" />
          {errText('invitationCode')}
        </label>
      )}

      {serverError && <p className="text-sm text-red-600">{serverError}</p>}

      <button disabled={busy} onClick={submit}
        className="mt-2 rounded-xl py-3 font-semibold text-white disabled:opacity-50"
        style={{ background: 'var(--brand-primary)' }}>
        {busy ? 'Création en cours…' : 'Créer mon restaurant'}
      </button>
    </section>
  );
}
